import React, { useEffect, useState } from "react";
import { Modal, Row, Col } from "react-bootstrap";
import {
  Button,
  Box,
  Divider,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  CircularProgress,
  Paper,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { API } from "../../../api/API";
import { toast } from "react-toastify";
import { appColors } from "../../../constant/constant";

const validationSchema = Yup.object().shape({
  nom: Yup.string().required("Le nom est obligatoire"),
  prenom: Yup.string().required("Le prénom est obligatoire"),
  tel: Yup.string()
    .required("Le téléphone est obligatoire")
    .matches(/^[0-9]+$/, "Doit contenir uniquement des chiffres")
    .min(8, "Doit contenir au moins 8 chiffres"),
  email: Yup.string().email("Email invalide"),
  adresse: Yup.string(),
  date_naissance: Yup.date()
    .required("La date de naissance est obligatoire")
    .max(new Date(), "La date ne peut pas être dans le futur"),
  role: Yup.string(),
  statut: Yup.boolean(),
  genre: Yup.string(),
  type_document_id: Yup.string(),
  numero_document_id: Yup.string(),
  date_delivre: Yup.string(),
  lieu_delivre: Yup.string(),
});

const DOCUMENT_TYPES = [
  { value: "CNI", label: "Carte Nationale d'Identité" },
  { value: "PASSPORT", label: "Passeport" },
];

const ROLES = [
  { value: "admin_assurance", label: "Admin Assurance" },
  { value: "agent_assurance", label: "Agent Assurance" },
  { value: "medecin_assurance", label: "Médecin Assurance" },
];

const UpdatePersonnelModal = ({
  show,
  handleClose,
  personnel,
  onUpdateSuccess,
}) => {
  const initialValues = {
    nom: "",
    prenom: "",
    tel: "",
    adresse: "",
    email: "",
    date_naissance: null,
    matricule: "",
    role: "",
    genre: "",
    type_document_id: "",
    numero_document_id: "",
    date_delivre: "",
    lieu_delivre: "",
    statut: false,
  };

  const [formValues, setFormValues] = useState(initialValues);
  const [isLoading, setIsLoading] = useState(false);
  const modalBodyRef = React.useRef(null);

  const fetchPersonnelData = async () => {
    if (!personnel) return;

    setIsLoading(true);
    try {
      const { id } = personnel;
      const response = await API.get(`/assurance/agents/${id}`);
      if (response.data?.data) {
        const data = response.data.data;
        setFormValues({
          nom: data.nom || "",
          prenom: data.prenom || "",
          tel: data.tel || "",
          adresse: data.adresse || "",
          email: data.email || "",
          date_naissance: data.date_naissance
            ? dayjs(data.date_naissance)
            : null,
          matricule: data.matricule || "",
          role: data.role || "",
          genre: data.genre || "",
          statut: data.statut || false,
          type_document_id: data.type_document_id || "",
          numero_document_id: data.numero_document_id || "",
          date_delivre: data.date_delivre ? dayjs(data.date_delivre) : null,
          lieu_delivre: data.lieu_delivre || "",
        });
      }
    } catch (error) {
      console.error("Error fetching personnel data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show && personnel) {
      fetchPersonnelData();
    } else {
      // Reset form when modal is closed or no personnel selected
      setFormValues(initialValues);
    }
  }, [show, personnel]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const formattedValues = {
        ...values,
        date_naissance: values.date_naissance?.format("YYYY-MM-DD"),
        date_delivre: values.date_delivre?.format("YYYY-MM-DD"),
      };
      //console.log("value", formattedValues);

      await API.put(`/assurance/agents/${personnel.id}`, formattedValues);
      toast.success("Informations mises à jour avec succès");
      onUpdateSuccess?.();
      handleClose();
    } catch (error) {
      console.error("Error updating personnel:", error);
      toast.error(
        error.response?.data?.message || "Erreur lors de la mise à jour"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!show || !personnel) return null;

  return (
    <Modal
      size="lg"
      show={show}
      onHide={handleClose}
      aria-labelledby="update-personnel-modal"
      centered
      backdrop="static"
      className="custom-modal"
    >
      <Paper
        sx={{ p: 3, height: "80vh", display: "flex", flexDirection: "column" }}
      >
        <Modal.Header closeButton sx={{ borderBottom: "none", pb: 0 }}>
          <Typography
            variant="h5"
            component="h2"
            fontWeight="bold"
            color="success"
          >
            {isLoading
              ? "Chargement..."
              : `Modifier ${formValues.prenom} ${formValues.nom}`}
          </Typography>
        </Modal.Header>

        <Modal.Body
          ref={modalBodyRef}
          style={{
            overflowY: "auto",
            flex: 1,
            padding: "0 16px",
            margin: "16px 0",
          }}
        >
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <CircularProgress />
            </Box>
          ) : (
            <Formik
              initialValues={formValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                setFieldValue,
                isSubmitting,
                handleBlur,
              }) => (
                <Form
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  {/* ... (le reste du formulaire reste inchangé) ... */}
                  <div style={{ flex: 1 }}>
                    {/* Section Informations personnelles */}
                    <Row className="mb-4">
                      <Col xs={12}>
                        <Typography variant="h6" gutterBottom color="success">
                          Informations personnelles
                        </Typography>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6} className="mb-3">
                        <TextField
                          fullWidth
                          label="Nom *"
                          name="nom"
                          value={values.nom}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.nom && Boolean(errors.nom)}
                          helperText={touched.nom && errors.nom}
                          variant="outlined"
                        />
                      </Col>

                      <Col md={6} className="mb-3">
                        <TextField
                          fullWidth
                          label="Prénom *"
                          name="prenom"
                          value={values.prenom}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.prenom && Boolean(errors.prenom)}
                          helperText={touched.prenom && errors.prenom}
                          variant="outlined"
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6} className="mb-3">
                        <TextField
                          fullWidth
                          label="Téléphone *"
                          name="tel"
                          value={values.tel}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.tel && Boolean(errors.tel)}
                          helperText={touched.tel && errors.tel}
                          variant="outlined"
                          inputProps={{ maxLength: 15 }}
                        />
                      </Col>

                      <Col md={6} className="mb-3">
                        <TextField
                          fullWidth
                          label="Email"
                          name="email"
                          type="email"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email && errors.email}
                          variant="outlined"
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col xs={12} className="mb-3">
                        <TextField
                          fullWidth
                          label="Adresse *"
                          name="adresse"
                          value={values.adresse}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.adresse && Boolean(errors.adresse)}
                          helperText={touched.adresse && errors.adresse}
                          variant="outlined"
                          multiline
                          rows={2}
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6} className="mb-3">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Date de naissance *"
                            value={
                              values.date_naissance
                                ? dayjs(values.date_naissance)
                                : null
                            }
                            onChange={(date) =>
                              setFieldValue("date_naissance", date)
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error:
                                  touched.date_naissance &&
                                  Boolean(errors.date_naissance),
                                helperText:
                                  touched.date_naissance &&
                                  errors.date_naissance,
                              },
                            }}
                            maxDate={dayjs()}
                          />
                        </LocalizationProvider>
                      </Col>

                      <Col md={6} className="mb-3">
                        <FormControl component="fieldset" fullWidth>
                          <FormLabel component="legend">Genre</FormLabel>
                          <RadioGroup
                            row
                            name="genre"
                            value={values.genre}
                            onChange={handleChange}
                          >
                            <FormControlLabel
                              value="homme"
                              control={<Radio />}
                              label="Masculin"
                            />
                            <FormControlLabel
                              value="femme"
                              control={<Radio />}
                              label="Féminin"
                            />
                          </RadioGroup>
                        </FormControl>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6} className="mb-3">
                        <TextField
                          select
                          fullWidth
                          label="Rôle"
                          name="role"
                          value={values.role}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.role && Boolean(errors.role)}
                          helperText={touched.role && errors.role}
                          variant="outlined"
                        >
                          <MenuItem value="">Sélectionnez un rôle</MenuItem>
                          {ROLES.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Col>

                      <Col md={6} className="mb-3">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.statut}
                              onChange={(e) =>
                                setFieldValue("statut", e.target.checked)
                              }
                              name="statut"
                              color="success"
                            />
                          }
                          label="Statut actif"
                        />
                      </Col>
                    </Row>

                    {/* Section Document d'identification */}
                    <Row className="mb-3">
                      <Col xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6" gutterBottom color="success">
                          Document d'identification
                        </Typography>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6} className="mb-3">
                        <TextField
                          select
                          fullWidth
                          label="Type de document"
                          name="type_document_id"
                          value={values.type_document_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.type_document_id &&
                            Boolean(errors.type_document_id)
                          }
                          helperText={
                            touched.type_document_id && errors.type_document_id
                          }
                          variant="outlined"
                        >
                          <MenuItem value="">Sélectionnez un type</MenuItem>
                          {DOCUMENT_TYPES.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Col>

                      <Col md={6} className="mb-3">
                        <TextField
                          fullWidth
                          label="Numéro de document"
                          name="numero_document_id"
                          value={values.numero_document_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.numero_document_id &&
                            Boolean(errors.numero_document_id)
                          }
                          helperText={
                            touched.numero_document_id &&
                            errors.numero_document_id
                          }
                          variant="outlined"
                        />
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md={6} className="mb-3">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            label="Date de délivrance"
                            value={
                              values.date_delivre
                                ? dayjs(values.date_delivre)
                                : null
                            }
                            onChange={(date) =>
                              setFieldValue("date_delivre", date)
                            }
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error:
                                  touched.date_delivre &&
                                  Boolean(errors.date_delivre),
                                helperText:
                                  touched.date_delivre && errors.date_delivre,
                              },
                            }}
                            maxDate={dayjs()}
                          />
                        </LocalizationProvider>
                      </Col>

                      <Col md={6} className="mb-3">
                        <TextField
                          fullWidth
                          label="Lieu de délivrance"
                          name="lieu_delivre"
                          value={values.lieu_delivre}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.lieu_delivre && Boolean(errors.lieu_delivre)
                          }
                          helperText={
                            touched.lieu_delivre && errors.lieu_delivre
                          }
                          variant="outlined"
                        />
                      </Col>
                    </Row>
                  </div>

                  {/* Actions - positionné en bas */}
                  <Row className="mt-4" style={{ marginTop: "auto" }}>
                    <Col xs={12}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 2,
                          pt: 2,
                          borderTop: "1px solid #eee",
                        }}
                      >
                        <Button
                          variant="outlined"
                          onClick={handleClose}
                          disabled={isSubmitting}
                          sx={{ px: 4 }}
                        >
                          Annuler
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={isSubmitting}
                          color="success"
                          sx={{ px: 4 }}
                          startIcon={
                            isSubmitting ? <CircularProgress size={20} /> : null
                          }
                        >
                          {isSubmitting ? "Enregistrement..." : "Enregistrer"}
                        </Button>
                      </Box>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          )}
        </Modal.Body>
      </Paper>
    </Modal>
  );
};

export default UpdatePersonnelModal;
